import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import NewsCard from "@/components/feature/NewsCard";
import ShareButton from "@/pages/match/components/ShareButton";
import { newsList } from "@/mocks/news";

export default function NewsDetail() {
  const { id } = useParams();
  const news = newsList.find((n) => n.id === id);

  useSeo({
    title: news ? `${news.title} - NBA直播吧新闻` : "新闻内容未找到 - NBA直播吧",
    description: news ? news.summary : "抱歉，您查看的新闻内容未找到。",
    keywords: news ? `${news.category},NBA新闻,NBA资讯,${news.title}` : "NBA新闻,NBA资讯",
  });

  if (!news) {
    return (
      <div className="w-full px-4 md:px-6 py-20 flex flex-col items-center text-center">
        <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl mb-5">
          <i className="ri-error-warning-line"></i>
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950">新闻内容未找到</h1>
        <p className="mt-2 text-sm text-foreground-500 max-w-md">该新闻可能已被移除或链接有误，请返回新闻列表查看其他内容。</p>
        <Link
          to="/news"
          className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i> 返回新闻列表
        </Link>
      </div>
    );
  }

  const related = news.relatedIds
    .map((rid) => newsList.find((n) => n.id === rid))
    .filter((n): n is (typeof newsList)[number] => Boolean(n));

  const hotList = newsList.filter((n) => n.id !== news.id).slice(0, 5);

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <div className="flex items-center gap-2 text-xs text-foreground-400 font-label mb-4 flex-wrap">
        <i className="ri-home-4-line"></i>
        <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/news" className="hover:text-primary-600 cursor-pointer">赛事新闻</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-600 line-clamp-1">{news.title}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <article className="lg:col-span-2 bg-background-50 rounded-lg border border-background-200 overflow-hidden">
          <div className="w-full h-56 md:h-80 overflow-hidden bg-background-100">
            <img
              src={news.image}
              alt={news.title}
              title={`${news.title} - NBA直播吧新闻`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="p-5 md:p-8">
            <span className="inline-block text-xs px-2.5 py-1 rounded bg-accent-100 text-accent-900 font-label">{news.category}</span>
            <h1 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">{news.title}</h1>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 pb-5 border-b border-background-200">
              <div className="flex items-center gap-4 text-xs text-foreground-500 font-label">
                <span className="flex items-center gap-1.5"><i className="ri-user-line"></i> {news.author}</span>
                <span className="flex items-center gap-1.5"><i className="ri-time-line"></i> {news.publishTime}</span>
              </div>
              <ShareButton />
            </div>

            <div className="mt-6 flex flex-col gap-4">
              {news.content.map((p, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-8 pt-5 border-t border-background-200 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500 font-label">相关标签：</span>
              {[news.category, "NBA", "篮球直播"].map((t) => (
                <span key={t} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label">
                  # {t}
                </span>
              ))}
            </div>
          </div>
        </article>

        <div className="lg:col-span-1 flex flex-col gap-6">
          <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
              <h2 className="font-heading text-base font-bold text-foreground-950">相关新闻推荐</h2>
            </div>
            <div className="flex flex-col gap-4">
              {related.length === 0 ? (
                <p className="py-4 text-sm text-foreground-400 text-center">暂无相关推荐</p>
              ) : (
                related.map((n) => <NewsCard key={n.id} news={n} variant="horizontal" />)
              )}
            </div>
          </div>

          <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
              <h2 className="font-heading text-base font-bold text-foreground-950">热门阅读</h2>
            </div>
            <div className="flex flex-col">
              {hotList.map((n) => (
                <NewsCard key={n.id} news={n} variant="compact" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}